import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { PALETTE } from '../tokens';
import { useAuth } from '../context/AuthContext';
import { rciApi } from '../api/rci';
import { insightsApi } from '../api/insights';
import { SignalScore } from '../components/SignalScore';
import { DomainSubScores } from '../components/DomainSubScores';
import { InsightCard } from '../components/InsightCard';
import { SafetyGatedView } from '../components/SafetyGatedView';
import SignalInfoButton from '../components/SignalInfoButton';
import { BottomNav } from '../components/BottomNav';
import { presentedScore } from '../utils/presentedScore';
import { Wordmark } from '../components/Wordmark';

const STYLES = `
  * { box-sizing: border-box; margin: 0; padding: 0; }

  .cd-root {
    font-family: 'DM Sans', sans-serif;
    background: ${PALETTE.bg};
    min-height: 100vh;
    color: ${PALETTE.text};
    max-width: 420px;
    margin: 0 auto;
  }
  .serif { font-family: 'Playfair Display', Georgia, serif; }

  @keyframes fadeUp {
    from { opacity: 0; transform: translateY(14px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50%      { opacity: 0.5; }
  }
  .fu  { animation: fadeUp 0.5s ease forwards; }
  .d1  { animation-delay: 0.05s; opacity: 0; }
  .d2  { animation-delay: 0.15s; opacity: 0; }
  .d3  { animation-delay: 0.25s; opacity: 0; }
  .d4  { animation-delay: 0.38s; opacity: 0; }

`;

export default function CoherenceDashboard() {
  const navigate = useNavigate();
  const { user, token } = useAuth();

  const [snapshot, setSnapshot] = useState(null);
  const [history, setHistory] = useState([]);
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!user) return;

    Promise.all([
      rciApi.snapshot(user.id, token).catch(() => null),
      rciApi.history(user.id, 30, token).catch(() => []),
      insightsApi.get(user.id, token).catch(() => null),
    ])
      .then(([snap, hist, ins]) => {
        setSnapshot(snap);
        setHistory(hist || []);
        setInsights(ins);
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [user, token]);

  // Derived data
  const coherenceStatus = snapshot?.coherence_status || 'CALIBRATING';
  const isSafetyGated = coherenceStatus === 'SAFETY_GATED';

  // User-facing presented score (re-anchored around 50 once baseline exists).
  // ``rci_total`` stays the canonical raw value in the API response.
  const score = snapshot ? presentedScore(snapshot) : null;

  // Days in app
  const daysInApp = snapshot?.total_snapshots || 0;

  // 7-day sparkline from history (rebased against the same baseline).
  const recent7 = [...(history || [])].reverse().slice(-7);
  const sparklineData = recent7.map(presentedScore);

  // Previous score for change framing (second-to-last snapshot)
  const previousScore =
    recent7.length >= 2 ? presentedScore(recent7[recent7.length - 2]) : null;

  // Domain scores (0.0–1.0)
  const bioScore = snapshot?.bio_score ?? null;
  const psychScore = snapshot?.psych_score ?? null;
  const behScore = snapshot?.beh_score ?? null;

  // Insight labels
  const bioLabel = insights?.bio_label || null;
  const psychLabel = insights?.psych_label || null;
  const behLabel = insights?.beh_label || null;
  const currentInsight = insights?.current_insight || null;

  // Full loading state
  if (loading) {
    return (
      <>
        <style>{STYLES}</style>
        <div className="cd-root">
          <div
            style={{
              position: 'relative',
              zIndex: 1,
              paddingBottom: 'var(--nav-clearance)',
            }}
          >
            <Header user={user} daysInApp={null} />
            <div style={{ padding: '0 24px' }}>
              <SignalScore loading />
              <div style={{ marginTop: 24 }}>
                <DomainSubScores loading />
              </div>
              <div style={{ marginTop: 16 }}>
                <InsightCard loading />
              </div>
            </div>
          </div>
          <BottomNav />
        </div>
      </>
    );
  }

  return (
    <>
      <style>{STYLES}</style>
      <div className="cd-root">
        {/* Subtle indigo wash */}
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: '50%',
            transform: 'translateX(-50%)',
            width: 420,
            height: 340,
            pointerEvents: 'none',
            zIndex: 0,
            background:
              'radial-gradient(ellipse at 40% 0%, rgba(158,132,86,0.09) 0%, transparent 68%)',
          }}
        />

        <div style={{ position: 'relative', zIndex: 1, paddingBottom: 'var(--nav-clearance)' }}>
          {/* Header */}
          <Header user={user} daysInApp={daysInApp || null} />

          {/* SAFETY_GATED: show grounding view only */}
          {isSafetyGated ? (
            <div className="fu d2">
              <SafetyGatedView />
            </div>
          ) : (
            <div style={{ padding: '0 24px' }}>
              {/* Signal Score */}
              <div className="fu d1">
                <SignalScore
                  score={score}
                  coherenceStatus={coherenceStatus}
                  sparklineData={sparklineData}
                  previousScore={previousScore}
                  daysInApp={daysInApp}
                  error={error}
                />
                {/* Score basis disclosure (Guideline 1.4.1) */}
                <div style={{ display: 'flex', justifyContent: 'center', marginTop: 4 }}>
                  <SignalInfoButton />
                </div>
              </div>

              {/* Domain Sub-Scores */}
              <div className="fu d2" style={{ marginTop: 24 }}>
                <DomainSubScores
                  bioScore={bioScore}
                  psychScore={psychScore}
                  behScore={behScore}
                  bioLabel={bioLabel}
                  psychLabel={psychLabel}
                  behLabel={behLabel}
                  coherenceStatus={coherenceStatus}
                  error={error}
                />
              </div>

              {/* Insight Card */}
              <div className="fu d3" style={{ marginTop: 16 }}>
                <InsightCard insight={currentInsight} />
              </div>
            </div>
          )}
        </div>

        {/* Chat FAB */}
        {!isSafetyGated && (
          <button
            onClick={() => navigate('/chat')}
            style={{
              position: 'fixed',
              bottom: 92,
              right: 24,
              zIndex: 20,
              width: 52,
              height: 52,
              borderRadius: '50%',
              background: PALETTE.primary,
              border: 'none',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 4px 16px rgba(158,132,86,0.35)',
              transition: 'opacity 0.15s',
            }}
          >
            <svg
              width="22"
              height="22"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#F2EDE3"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
            </svg>
          </button>
        )}

        {/* Bottom Nav */}
        <BottomNav />
      </div>
    </>
  );
}

/* ------------------------------------------------------------------ */
/* Header                                                             */
/* ------------------------------------------------------------------ */
function Header({ user, daysInApp }) {
  return (
    <div
      className="fu d1"
      style={{
        padding: 'max(54px, calc(env(safe-area-inset-top) + 12px)) 24px 0',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
      }}
    >
      <div>
        <Wordmark />
        <div style={{ fontSize: 13, color: PALETTE.textMute, fontWeight: 300 }}>
          {new Date().toLocaleDateString('en-US', {
            weekday: 'long',
            month: 'long',
            day: 'numeric',
          })}
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        {daysInApp != null && (
          <div
            style={{
              fontSize: 10,
              color: PALETTE.primary,
              border: `1px solid ${PALETTE.border}`,
              padding: '4px 11px',
              borderRadius: 99,
              letterSpacing: '0.07em',
              fontWeight: 500,
            }}
          >
            Day {daysInApp}
          </div>
        )}
        <div
          style={{
            width: 36,
            height: 36,
            borderRadius: '50%',
            background: PALETTE.surfaceAlt,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 14,
            color: PALETTE.primary,
            fontFamily: "'Playfair Display', serif",
            fontStyle: 'italic',
          }}
        >
          {user?.full_name?.[0]?.toUpperCase() || '?'}
        </div>
      </div>
    </div>
  );
}

